import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { ArrowRight } from "lucide-react";

import { PageParams } from "../ai-voices/ConfigureVoiceModal";
import { useOrganisation } from "../../http-hooks/organisation/useOrganisation";
import { toast } from "react-toastify";
import { useGlobalStore } from "../../zustand-store/useGlobalStore";
import { addMixPanelEvent } from "../../store/actions";
import { useDispatch } from "react-redux";
import { Modal as CustomModal, ModalHeader, ModalTitle, ModalBody } from "../ui/Modal";
import Spinner from "../Spinner";
import PlanCardsGrid, { getPlanTier } from "./PlanCardsGrid";

interface IPlansModalProps {
  showCurrentPlan?: boolean
}

export const UpgradePlanModalV2 = ({ showCurrentPlan }: IPlansModalProps) => {

  const dispatch = useDispatch()
  // one ID per prompt appearance, shared across shown/clicked/dismissed so we can
  // tell how many of a given prompt actually get clicked
  const [promptId, setPromptId] = useState<string | null>(null);
  const { org_id } = useParams<PageParams>();
  const { data: org, isLoading, error } = useOrganisation({ org_id });

  // Use shallow equality to prevent unnecessary re-renders
  const { show, toggle, title, modalSource, isManualTrigger } = useStoreWithEqualityFn(
    useGlobalStore,
    (state) => ({
      show: state.showUpgradeModal,
      toggle: state.toggleUpgradeModal,
      title: state.modalTitle,
      modalSource: state.modalSource,
      isManualTrigger: state.isManualTrigger,
    }),
    shallow
  );

  // GROW-73: guard so "Upgrade Prompt Dismissed" is emitted at most once per open,
  // even if the close handler fires more than once (Esc+overlay, rapid double-click, etc.).
  const dismissedRef = useRef(false);

  useEffect(() => {
    if(show){
      const upgradeSource = modalSource || 'unknown';
      const newPromptId = `${org_id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setPromptId(newPromptId);
      dismissedRef.current = false; // fresh prompt → allow one dismissal

      // Track when user views the upgrade modal (manual or automatic)
      dispatch(addMixPanelEvent({
        orgId: org_id,
        body: {
          eventName: "PAYMENT_VIEW_PLANS",
          eventData: {
            source: upgradeSource,
            is_manual: isManualTrigger,
            plan_id: org?.plan_id,
            plan_tier: getPlanTier(org?.plan_id),
            words_remaining: org?.words_remaining
          }
        }
      }))
      dispatch(addMixPanelEvent({
        orgId: org_id,
        body: {
          eventName: "UPGRADE_PROMPT_SHOWN",
          eventData: {
            upgrade_prompt_id: newPromptId,
            upgrade_source: upgradeSource,
            is_manual: isManualTrigger,
            plan_tier: getPlanTier(org?.plan_id),
          }
        }
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Fires only for an explicit user dismissal (X / overlay / Esc) — successful
  // upgrades close the modal via handleSuccess -> toggle(false) instead.
  function onHide() {
    if (show && !dismissedRef.current) {
      dismissedRef.current = true;
      const upgradeSource = modalSource || 'unknown';
      dispatch(addMixPanelEvent({
        orgId: org_id,
        body: {
          eventName: "UPGRADE_PROMPT_DISMISSED",
          eventData: {
            upgrade_prompt_id: promptId,
            upgrade_source: upgradeSource,
            plan_tier: getPlanTier(org?.plan_id),
          }
        }
      }))
    }
    toggle(false)
  }


  useEffect(() => {
    if (error) {
      toast.error("Something went wrong!");
      toggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading) {
    return (
      <Spinner color="var(--bs-secondary-color)" />
    );
  }

  if (error) {
    return <></>
  }

  const handleSuccess = () => {
    removeModalFromURL();
    toggle(false);
    window.location.reload();
  };

  return (
    <CustomModal
      className="plans-modal"
      isOpen={show}
      onClose={onHide}
      size="6xl"
      centered={true}
      closeOnOverlayClick={true}
      closeOnEsc={true}
      overlayClassName="plans-modal-overlay"
    >
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody className="px-3 px-md-4 pb-4 pt-2">
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <PlanCardsGrid
          org_id={org_id}
          onSuccess={handleSuccess}
          source={modalSource || "upgrade_modal"}
          upgradePromptId={promptId}
        />

        {/* Footer */}
        <div className="text-end mt-3">
          <a href="https://getlisten2it.com/pricing" className="text-decoration-none small text-primary fw-semibold">
            See full comparison <ArrowRight size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
          </a>
        </div>
        </div>
      </ModalBody>
    </CustomModal>
  )
}

const removeModalFromURL = () => {
  window.history.replaceState(null, "", window.location.href.split('?')[0])
}

